import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { Checkbox } from '@shared/components/ui/shadcn/checkbox';
import { Label } from '@shared/components/ui/shadcn/label';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import { Info, Minus, Plus } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  COURIER_INCIDENCE_TYPES,
  IdentifiedOrder,
  IncidenceType,
  REJECTION_REASONS,
  RejectionReason,
} from '../../models';

// Función auxiliar para construir URL completa de imagen
const buildImageUrl = (imageHash?: string) => {
  if (!imageHash) return '';
  if (imageHash.startsWith('http')) return imageHash;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
};

interface MissingProduct {
  lineId: number;
  productId: number;
  name: string;
  code: string;
  image: string;
  missingQty: number;
  rejectionKey: string; // Clave única para rechazos: `${lineId}` para productos individuales, `${lineId}:${productId}` para packs
  isPack: boolean;
  packInfo?: string; // Info del pack si viene de un pack
}

interface RejectionEntry {
  quantity: number;
  reason: RejectionReason | '';
}

export interface IncidenceModalRef {
  open: (
    order: IdentifiedOrder,
    scannedQuantities: Record<string, number>,
    onConfirm: (data: {
      hasCourierIncidence: boolean;
      incidenceType: IncidenceType | null;
      rejectionReasons: Record<string | number, RejectionReason>;
      rejectedQuantities: Record<string | number, number>;
    }) => Promise<void>,
  ) => void;
}

export const IncidenceModal = forwardRef<IncidenceModalRef>((_, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const [order, setOrder] = useState<IdentifiedOrder | null>(null);
  const [scannedQuantities, setScannedQuantities] = useState<Record<string, number>>({});
  const [hasCourierIncidence, setHasCourierIncidence] = useState(false);
  const [incidenceType, setIncidenceType] = useState<IncidenceType | null>(null);

  // Nuevo: múltiples entradas de rechazo por producto (clave es string para soportar packs)
  const [rejectionEntries, setRejectionEntries] = useState<Record<string, RejectionEntry[]>>({});

  const [isProcessing, setIsProcessing] = useState(false);
  const confirmCallbackRef = useRef<
    | ((data: {
        hasCourierIncidence: boolean;
        incidenceType: IncidenceType | null;
        rejectionReasons: Record<number, RejectionReason>;
        rejectedQuantities: Record<number, number>;
      }) => Promise<void>)
    | null
  >(null);

  useImperativeHandle(ref, () => ({
    open: (
      orderData: IdentifiedOrder,
      quantities: Record<string, number>,
      onConfirm: (data: {
        hasCourierIncidence: boolean;
        incidenceType: IncidenceType | null;
        rejectionReasons: Record<number, RejectionReason>;
        rejectedQuantities: Record<number, number>;
      }) => Promise<void>,
    ) => {
      setOrder(orderData);
      setScannedQuantities(quantities);
      setHasCourierIncidence(false);
      setIncidenceType(null);

      // Inicializar entradas de rechazo: una entrada por defecto por cada producto faltante
      const initialEntries: Record<string, RejectionEntry[]> = {};

      orderData.lines.forEach((line) => {
        // Si la línea tiene un pack, expandir a productos individuales
        if (line.pack && line.pack.lines) {
          const packQty = line.qty;

          line.pack.lines.forEach((packLine) => {
            const productId = packLine.product_id;
            if (!productId) return;

            // Clave para escaneos de productos dentro de packs
            const scannedKey = `${orderData.id}:${line.id}:${productId}`;
            const scannedQty = quantities[scannedKey] || 0;

            // Cantidad esperada: cantidad del pack * cantidad del producto en el pack
            const expectedQty = packQty * packLine.qty;
            const missingQty = expectedQty - scannedQty;

            if (missingQty > 0) {
              // Clave única para rechazos: incluye lineId y productId
              const rejectionKey = `${line.id}:${productId}`;
              initialEntries[rejectionKey] = [{ quantity: missingQty, reason: '' }];
            }
          });
        } else if (line.product_id) {
          // Producto individual (no pack)
          const scannedKey = `${orderData.id}:${line.id}`;
          const scannedQty = quantities[scannedKey] || 0;
          const missingQty = line.qty - scannedQty;

          if (missingQty > 0) {
            // Clave única para rechazos: solo lineId para productos individuales
            const rejectionKey = `${line.id}`;
            initialEntries[rejectionKey] = [{ quantity: missingQty, reason: '' }];
          }
        }
      });
      setRejectionEntries(initialEntries);

      setIsProcessing(false);
      confirmCallbackRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  // Calcular productos faltantes
  const getMissingProducts = (): MissingProduct[] => {
    if (!order?.lines) return [];

    const missingProducts: MissingProduct[] = [];

    order.lines.forEach((line) => {
      // Si la línea tiene un pack, expandir a productos individuales
      if (line.pack && line.pack.lines) {
        const packQty = line.qty;

        line.pack.lines.forEach((packLine) => {
          const productId = packLine.product_id;
          if (!productId) return;

          // Clave para escaneos de productos dentro de packs
          const scannedKey = `${order.id}:${line.id}:${productId}`;
          const scannedQty = scannedQuantities[scannedKey] || 0;

          // Cantidad esperada: cantidad del pack * cantidad del producto en el pack
          const expectedQty = packQty * packLine.qty;
          const missingQty = expectedQty - scannedQty;

          if (missingQty > 0) {
            missingProducts.push({
              lineId: line.id,
              productId,
              name: packLine.product?.name || `Producto ID: ${productId}`,
              code: packLine.product?.code || '',
              image: buildImageUrl(packLine.product?.image || packLine.product?.image_hash),
              missingQty,
              rejectionKey: `${line.id}:${productId}`, // Clave única para rechazos
              isPack: true,
              packInfo: line.pack.title,
            });
          }
        });
      } else if (line.product_id) {
        // Producto individual (no pack)
        const scannedKey = `${order.id}:${line.id}`;
        const scannedQty = scannedQuantities[scannedKey] || 0;
        const missingQty = line.qty - scannedQty;

        if (missingQty > 0) {
          missingProducts.push({
            lineId: line.id,
            productId: line.product_id,
            name: line.product?.name || line.name || `Producto ID: ${line.product_id}`,
            code: line.product?.code || '',
            image: buildImageUrl(line.product?.image || line.product?.image_hash),
            missingQty,
            rejectionKey: `${line.id}`, // Clave única para rechazos
            isPack: false,
          });
        }
      }
    });

    return missingProducts;
  };

  const missingProducts = getMissingProducts();

  // Validar si se puede confirmar
  const canConfirm = () => {
    if (hasCourierIncidence) {
      return incidenceType !== null;
    } else {
      // Validar que TODAS las unidades faltantes estén rechazadas
      return missingProducts.every((p) => {
        const entries = rejectionEntries[p.rejectionKey] || [];
        if (entries.length === 0) return false;

        // Todas las entradas deben tener razón y cantidad > 0
        const allValid = entries.every((e) => e.reason !== '' && e.quantity > 0);
        if (!allValid) return false;

        // La suma de cantidades debe ser EXACTAMENTE igual a missingQty
        const totalRejected = entries.reduce((sum, e) => sum + e.quantity, 0);
        return totalRejected === p.missingQty;
      });
    }
  };

  const handleConfirm = async () => {
    if (confirmCallbackRef.current) {
      setIsProcessing(true);

      try {
        // Convertir entradas múltiples a formato antiguo (usar primera entrada como principal)
        const rejectionReasons: Record<string | number, RejectionReason> = {};
        const rejectedQuantities: Record<string | number, number> = {};

        if (!hasCourierIncidence) {
          Object.entries(rejectionEntries).forEach(([rejectionKey, entries]) => {
            // Sumar todas las cantidades rechazadas por clave
            const totalQty = entries.reduce((sum, e) => sum + e.quantity, 0);

            // La clave puede ser `${lineId}` para productos individuales o `${lineId}:${productId}` para packs
            // Convertir a número para mantener compatibilidad con el formato esperado
            // Para packs, usamos la clave completa como string
            if (rejectionKey.includes(':')) {
              // Es un pack: usar la clave completa
              rejectedQuantities[rejectionKey as any] = totalQty;
              if (entries.length > 0 && entries[0].reason) {
                rejectionReasons[rejectionKey as any] = entries[0].reason as RejectionReason;
              }
            } else {
              // Es un producto individual: usar solo el número
              rejectedQuantities[parseInt(rejectionKey)] = totalQty;
              if (entries.length > 0 && entries[0].reason) {
                rejectionReasons[parseInt(rejectionKey)] = entries[0].reason as RejectionReason;
              }
            }
          });
        }

        await confirmCallbackRef.current({
          hasCourierIncidence,
          incidenceType: hasCourierIncidence ? incidenceType : null,
          rejectionReasons: hasCourierIncidence ? {} : rejectionReasons,
          rejectedQuantities: hasCourierIncidence ? {} : rejectedQuantities,
        });

        // Si llegamos aquí, fue exitoso - cerrar modal
        modalRef.current?.closeModal();
      } catch (error) {
        // Si hay error, cerrar modal y resetear estado
        console.error('Error al confirmar recepción:', error);
        modalRef.current?.closeModal();
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleClose = () => {
    modalRef.current?.closeModal();
    setIsProcessing(false);
  };

  return (
    <ModalCenter
      ref={modalRef}
      title="Productos Faltantes"
      type="warning"
      width="xxl"
      onCloseModal={() => setIsProcessing(false)}
    >
      <div className="space-y-6">
        {/* Subtítulo */}
        <div className="flex items-center gap-2 pb-4 border-b border-border">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30">
            <span className="text-sm font-bold text-orange-600 dark:text-orange-400">{missingProducts.length}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            producto{missingProducts.length !== 1 ? 's' : ''} sin escanear
          </p>
        </div>

        {/* Opción de incidencia de courier */}
        <div
          className={`rounded-xl border-2 p-4 transition-all ${
            hasCourierIncidence
              ? 'bg-orange-50 dark:bg-orange-950/20 border-orange-300 dark:border-orange-700'
              : 'bg-muted/30 border-border hover:border-muted-foreground/30'
          }`}
        >
          <div className="flex items-start gap-3">
            <Checkbox
              id="courier-incidence"
              checked={hasCourierIncidence}
              onCheckedChange={(checked) => setHasCourierIncidence(checked === true)}
              disabled={isProcessing}
              className="mt-1"
            />
            <div className="flex-1">
              <Label
                htmlFor="courier-incidence"
                className="text-base font-semibold text-foreground cursor-pointer block"
              >
                Incidencia de courier
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Marca si el problema es del transporte y no del producto
              </p>
            </div>
          </div>

          {/* Selector de tipo de incidencia */}
          {hasCourierIncidence && (
            <div className="mt-4 pl-8 animate-in fade-in slide-in-from-top-2 duration-200">
              <Label className="block text-sm font-medium text-foreground mb-2">Tipo de incidencia</Label>
              <Select
                value={incidenceType || ''}
                onValueChange={(value) => setIncidenceType(value as IncidenceType)}
                disabled={isProcessing}
              >
                <SelectTrigger className={`w-full ${!incidenceType ? 'border-orange-500/50' : ''}`}>
                  <SelectValue placeholder="Selecciona el tipo de incidencia..." />
                </SelectTrigger>
                <SelectContent>
                  {COURIER_INCIDENCE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Lista de productos faltantes con motivos de rechazo */}
        {!hasCourierIncidence && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold text-foreground">Motivos de rechazo</Label>
              <span className="text-xs text-muted-foreground italic">Puedes añadir múltiples motivos por producto</span>
            </div>

            <ScrollArea className="h-96">
              <div className="space-y-3 pr-4">
                {missingProducts.map((product) => {
                  const entries = rejectionEntries[product.rejectionKey] || [];
                  const totalRejected = entries.reduce((sum, e) => sum + e.quantity, 0);
                  const remaining = product.missingQty - totalRejected;

                  return (
                    <div
                      key={product.rejectionKey}
                      className="bg-muted/20 rounded-xl p-4 space-y-3 border border-transparent hover:border-border transition-colors"
                    >
                      {/* Header del producto */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <Product
                              id={product.productId}
                              src={product.image}
                              name={product.name}
                              code={product.code}
                              acronym={product.name?.charAt(0).toUpperCase()}
                              showImageModal={true}
                              enableLineClamp={true}
                            />
                            {product.packInfo && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                                Pack: {product.packInfo}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end flex-shrink-0">
                          <span className="text-sm font-semibold text-foreground">
                            {totalRejected} / {product.missingQty}
                          </span>
                          <span className="text-xs text-muted-foreground">unidades</span>
                          {remaining > 0 && (
                            <span className="text-xs font-medium text-orange-600 dark:text-orange-400 mt-1">
                              Faltan {remaining}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Entradas de rechazo */}
                      <div className="space-y-2 pl-14">
                        {entries.map((entry, index) => (
                          <div key={index} className="flex items-center gap-2 group">
                            {/* Cantidad */}
                            <div className="flex items-center gap-0.5 bg-background rounded-lg border border-border p-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 hover:bg-muted"
                                onClick={() => {
                                  if (entry.quantity > 1) {
                                    const newEntries = [...entries];
                                    newEntries[index] = { ...entry, quantity: entry.quantity - 1 };
                                    setRejectionEntries((prev) => ({ ...prev, [product.rejectionKey]: newEntries }));
                                  }
                                }}
                                disabled={isProcessing || entry.quantity <= 1}
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </Button>
                              <div className="w-12 text-center">
                                <span className="text-sm font-medium">{entry.quantity}</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 hover:bg-muted"
                                onClick={() => {
                                  if (totalRejected < product.missingQty) {
                                    const newEntries = [...entries];
                                    newEntries[index] = { ...entry, quantity: entry.quantity + 1 };
                                    setRejectionEntries((prev) => ({ ...prev, [product.rejectionKey]: newEntries }));
                                  }
                                }}
                                disabled={isProcessing || totalRejected >= product.missingQty}
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>

                            {/* Motivo */}
                            <div className="flex-1">
                              <Select
                                value={entry.reason}
                                onValueChange={(value) => {
                                  const newEntries = [...entries];
                                  newEntries[index] = { ...entry, reason: value as RejectionReason };
                                  setRejectionEntries((prev) => ({ ...prev, [product.rejectionKey]: newEntries }));
                                }}
                                disabled={isProcessing}
                              >
                                <SelectTrigger
                                  className={`h-9 ${!entry.reason ? 'border-orange-500/50 bg-orange-50/50 dark:bg-orange-950/20' : ''}`}
                                >
                                  <SelectValue placeholder="Selecciona motivo..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {REJECTION_REASONS.map((reason) => (
                                    <SelectItem key={reason.value} value={reason.value}>
                                      {reason.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Botón eliminar entrada */}
                            {entries.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => {
                                  const newEntries = entries.filter((_, i) => i !== index);
                                  setRejectionEntries((prev) => ({ ...prev, [product.rejectionKey]: newEntries }));
                                }}
                                disabled={isProcessing}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </Button>
                            )}
                          </div>
                        ))}

                        {/* Botón añadir otro motivo */}
                        {remaining > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full h-8 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-dashed border-border"
                            onClick={() => {
                              const newEntries: RejectionEntry[] = [
                                ...entries,
                                { quantity: Math.min(1, remaining), reason: '' as const },
                              ];
                              setRejectionEntries((prev) => ({ ...prev, [product.rejectionKey]: newEntries }));
                            }}
                            disabled={isProcessing}
                          >
                            <Plus className="h-3.5 w-3.5 mr-1.5" />
                            Añadir otro motivo
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Info cuando hay incidencia de courier */}
        {hasCourierIncidence && (
          <Alert className="bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-300 dark:border-blue-700">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-700 dark:text-blue-300">
              Al marcar incidencia de courier, <strong>todos los productos faltantes</strong> se rechazarán
              automáticamente.
            </AlertDescription>
          </Alert>
        )}

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-6 border-t border-border">
          <Button variant="outline" size="lg" onClick={handleClose} disabled={isProcessing} className="min-w-[120px]">
            Cancelar
          </Button>
          <Button
            size="lg"
            onClick={handleConfirm}
            disabled={!canConfirm() || isProcessing}
            className="min-w-[180px] bg-primary hover:bg-primary/90"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2"></div>
                Procesando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Confirmar Recepción
              </>
            )}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

IncidenceModal.displayName = 'IncidenceModal';
